import React, { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Crown, Gift, Loader2, Check, X, RotateCcw, Sparkles, Trash2, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const PLAN_PRESETS = [
  { id: "basic", label: "Bàsic", label_es: "Básico",
    color: "bg-blue-600 hover:bg-blue-700" },
  { id: "pro", label: "Pro", label_es: "Pro",
    color: "bg-emerald-600 hover:bg-emerald-700" },
  { id: "enterprise", label: "Enterprise", label_es: "Enterprise",
    color: "bg-purple-600 hover:bg-purple-700" },
];

const DURATION_PRESETS = [
  { d: 14, ca: "14 dies (trial)", es: "14 días (trial)" },
  { d: 30, ca: "30 dies (1 mes)", es: "30 días (1 mes)" },
  { d: 90, ca: "90 dies (3 mesos)", es: "90 días (3 meses)" },
  { d: 365, ca: "365 dies (1 any)", es: "365 días (1 año)" },
  { d: 3650, ca: "Lifetime (10 anys)", es: "Lifetime (10 años)" },
];

export default function AdminCompanyEditor({ open, company, onClose, onChanged }) {
  const { lang } = useI18n();
  const [plan, setPlan] = useState("pro");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(null);
  // Hard-delete confirmation state
  const [showDelete, setShowDelete] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [confirmWord, setConfirmWord] = useState("");

  useEffect(() => {
    if (company) {
      setPlan(company.plan && ["basic","pro","enterprise"].includes(company.plan)
              ? company.plan : "pro");
      setDays(30);
      setShowDelete(false);
      setConfirmName("");
      setConfirmWord("");
    }
  }, [company]);

  if (!company) return null;

  const tr = (ca, es) => (lang === "es" ? es : ca);

  const setPlanAction = async () => {
    setBusy("set");
    try {
      const r = await api.post(`/admin/companies/${company.id}/set-plan`,
                               { plan, days: Number(days) });
      toast.success(tr(
        `Pla ${plan} assignat · ${r.data.days}d`,
        `Plan ${plan} asignado · ${r.data.days}d`));
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const clearPlan = async () => {
    if (!window.confirm(tr(
      "Treure tot accés a aquesta empresa?",
      "¿Quitar todo acceso a esta empresa?"))) return;
    setBusy("clear");
    try {
      await api.post(`/admin/companies/${company.id}/clear-plan`);
      toast.success(tr("Subscripció netejada", "Suscripción limpiada"));
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const toggleOwner = async (val) => {
    setBusy(val ? "owner-on" : "owner-off");
    try {
      await api.post(`/admin/companies/${company.id}/set-owner`,
                     { is_owner: val });
      toast.success(val
        ? tr("Marcat com a propietat (Enterprise gratis)",
             "Marcado como propiedad (Enterprise gratis)")
        : tr("Ja no és propietat", "Ya no es propiedad"));
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const hardDelete = async () => {
    setBusy("delete");
    try {
      const r = await api.delete(`/admin/companies/${company.id}`,
        { data: { confirm_name: confirmName, confirm_word: confirmWord } });
      const cc = r.data.cascade_counts || {};
      const total = Object.values(cc).reduce((a, b) => a + b, 0);
      toast.success(tr(
        `Empresa "${r.data.deleted_company}" eliminada · ${total} registres associats`,
        `Empresa "${r.data.deleted_company}" eliminada · ${total} registros asociados`));
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const deleteValid = confirmName === company.name &&
                      confirmWord.trim().toUpperCase() === "DELETE";

  const currentDesc = (() => {
    if (company.is_owner) return tr("Propietat · Enterprise gratis", "Propiedad · Enterprise gratis");
    if (!company.plan) return tr("Sense subscripció", "Sin suscripción");
    const d = company.days_remaining;
    const expired = d != null && d <= 0;
    if (expired) return tr(`${company.plan} (expirat)`, `${company.plan} (expirado)`);
    const trial = company.is_trial ? " · TRIAL" : "";
    return `${company.plan} · ${Math.floor(d ?? 0)}d${trial}`;
  })();

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right"
                    className="w-full sm:max-w-md bg-[#0a0f1c] border-l border-white/10 text-white overflow-y-auto"
                    data-testid="admin-company-editor">
        <SheetHeader>
          <SheetTitle className="text-white font-display">
            {company.name}
          </SheetTitle>
          <SheetDescription className="text-slate-400 flex flex-col gap-1">
            <span className="font-mono text-xs">{company.nif || "—"}</span>
            <span className="text-xs">{tr("Actual:", "Actual:")} <strong>{currentDesc}</strong></span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Owner toggle */}
          <section className="space-y-2"
                   data-testid="admin-section-owner">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
              <Crown size={12} className="text-amber-400" />
              {tr("Propietat HOSYS", "Propiedad HOSYS")}
            </div>
            <p className="text-xs text-slate-500">
              {tr(
                "Les empreses propietat obtenen Enterprise gratis de per vida.",
                "Las empresas propiedad obtienen Enterprise gratis de por vida.")}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busy === "owner-on" || company.is_owner}
                onClick={() => toggleOwner(true)}
                className="flex-1 bg-amber-500/10 border-amber-500/40 text-amber-100 hover:bg-amber-500/20 disabled:opacity-40"
                data-testid="admin-set-owner-on">
                {busy === "owner-on" ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : <Crown size={14} className="mr-2" />}
                {tr("Marcar com a propietat", "Marcar como propiedad")}
              </Button>
              {company.is_owner && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === "owner-off"}
                  onClick={() => toggleOwner(false)}
                  className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                  data-testid="admin-set-owner-off">
                  {busy === "owner-off" ? <Loader2 size={14} className="animate-spin" />
                    : <X size={14} />}
                </Button>
              )}
            </div>
          </section>

          {/* Plan grant */}
          <section className="space-y-3"
                   data-testid="admin-section-plan">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
              <Sparkles size={12} className="text-purple-400" />
              {tr("Concedir pla manualment", "Conceder plan manualmente")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {PLAN_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlan(p.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                    plan === p.id
                      ? `${p.color} text-white border-transparent`
                      : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                  }`}
                  data-testid={`admin-plan-${p.id}`}>
                  {lang === "es" ? p.label_es : p.label}
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">
                {tr("Durada", "Duración")}
              </Label>
              <div className="grid grid-cols-2 gap-1.5">
                {DURATION_PRESETS.map((d) => (
                  <button
                    key={d.d}
                    onClick={() => setDays(d.d)}
                    className={`px-2.5 py-1.5 rounded-md text-xs border transition ${
                      days === d.d
                        ? "bg-blue-500/20 border-blue-500/50 text-blue-100"
                        : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                    }`}
                    data-testid={`admin-days-${d.d}`}>
                    {lang === "es" ? d.es : d.ca}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px] text-slate-500">{tr("o personalitzat:", "o personalizado:")}</span>
                <Input
                  type="number"
                  min={1}
                  max={3650}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="h-7 w-24 text-xs bg-white/5 border-white/10 text-white"
                  data-testid="admin-days-custom" />
                <span className="text-[11px] text-slate-500">{tr("dies", "días")}</span>
              </div>
            </div>

            <Button
              onClick={setPlanAction}
              disabled={!!busy}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              data-testid="admin-apply-plan">
              {busy === "set" ? (
                <><Loader2 size={14} className="mr-2 animate-spin" />{tr("Aplicant...", "Aplicando...")}</>
              ) : (
                <><Gift size={14} className="mr-2" />
                {tr(`Aplicar ${plan} · ${days}d`, `Aplicar ${plan} · ${days}d`)}</>
              )}
            </Button>
          </section>

          {/* Reset */}
          {company.plan && (
            <section className="pt-4 border-t border-white/5"
                     data-testid="admin-section-reset">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-300/80 mb-2">
                <RotateCcw size={12} />
                {tr("Zona perillosa", "Zona peligrosa")}
              </div>
              <Button
                onClick={clearPlan}
                disabled={!!busy}
                variant="outline"
                className="w-full bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
                data-testid="admin-clear-plan">
                {busy === "clear" ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : <Trash2 size={14} className="mr-2" />}
                {tr("Treure subscripció", "Quitar suscripción")}
              </Button>
            </section>
          )}

          {/* Hard delete — irreversible */}
          <section className="pt-4 mt-2 border-t border-rose-500/20"
                   data-testid="admin-section-delete">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-300 mb-3">
              <AlertTriangle size={12} />
              {tr("Eliminar empresa permanentment",
                  "Eliminar empresa permanentemente")}
            </div>
            {!showDelete ? (
              <>
                <p className="text-xs text-slate-500 leading-relaxed mb-3">
                  {tr(
                    "Aquesta acció esborra l'empresa i totes les seves factures, pressupostos, clients i registres relacionats. NO es pot desfer.",
                    "Esta acción borra la empresa y todas sus facturas, presupuestos, clientes y registros relacionados. NO se puede deshacer."
                  )}
                </p>
                <Button
                  onClick={() => setShowDelete(true)}
                  variant="outline"
                  className="w-full bg-rose-600/15 border-rose-500/40 text-rose-200 hover:bg-rose-600/25"
                  data-testid="admin-delete-show">
                  <Trash2 size={14} className="mr-2" />
                  {tr("Eliminar empresa…", "Eliminar empresa…")}
                </Button>
              </>
            ) : (
              <div className="space-y-3 rounded-lg bg-rose-500/5 border border-rose-500/40 p-3"
                   data-testid="admin-delete-form">
                <div className="flex items-start gap-2 text-xs text-rose-200 leading-relaxed">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {tr(
                      "Confirmació en 2 passos. Aquesta acció és IRREVERSIBLE.",
                      "Confirmación en 2 pasos. Esta acción es IRREVERSIBLE."
                    )}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-400">
                    {tr("1. Escriu el nom exacte de l'empresa:",
                        "1. Escribe el nombre exacto de la empresa:")}
                  </Label>
                  <div className="text-[11px] font-mono text-slate-500 px-2 py-1 rounded bg-black/30">
                    {company.name}
                  </div>
                  <Input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={company.name}
                    className="h-8 bg-white/5 border-white/10 text-white text-sm font-mono"
                    data-testid="admin-delete-confirm-name" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-400">
                    {tr("2. Escriu DELETE per confirmar:",
                        "2. Escribe DELETE para confirmar:")}
                  </Label>
                  <Input
                    value={confirmWord}
                    onChange={(e) => setConfirmWord(e.target.value)}
                    placeholder="DELETE"
                    className="h-8 bg-white/5 border-white/10 text-white text-sm font-mono uppercase tracking-widest"
                    data-testid="admin-delete-confirm-word" />
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={() => {
                      setShowDelete(false);
                      setConfirmName("");
                      setConfirmWord("");
                    }}
                    variant="outline"
                    disabled={busy === "delete"}
                    className="flex-1 bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 h-9"
                    data-testid="admin-delete-cancel">
                    {tr("Cancel·lar", "Cancelar")}
                  </Button>
                  <Button
                    onClick={hardDelete}
                    disabled={!deleteValid || busy === "delete"}
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 h-9"
                    data-testid="admin-delete-confirm">
                    {busy === "delete" ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <><Trash2 size={14} className="mr-2" />
                      {tr("Eliminar definitivament",
                          "Eliminar definitivamente")}</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
